import type React from "react";
import type { ArticleBlock } from "@/content/types";
import { Info } from "lucide-react";

import { withPageImages } from "@/content/page-images";

import {
  LINK_TARGETS,
  LinkBudget,
  REQUIRED_LINK_PATHS,
  WEAK_LINK_PATHS,
  linkifyText,
  textMentions,
} from "@/lib/internal-links";

function slugify(text: string) {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/**
 * Verdeelt interne links over de hele tekst: elk linkdoel dat organisch in de
 * body voorkomt krijgt één link, in het eerste nog vrije blok dat het noemt.
 * Verplichte doelen en de money pages van dit artikel gaan voor.
 */
function planRequiredLinks(
  blocks: ArticleBlock[],
  currentPath: string,
  priorityPaths: readonly string[] = [],
  maxLinks = 12,
) {
  const plan = new Map<number, string>();
  const norm = (p: string) => p.replace(/\/+$/, "");
  const ordered = [
    ...REQUIRED_LINK_PATHS,
    ...priorityPaths,
    ...WEAK_LINK_PATHS.filter((p) => LINK_TARGETS.some((t) => norm(t.path) === norm(p))),
    ...LINK_TARGETS.map((t) => t.path),
  ].filter((p, i, arr) => arr.findIndex((x) => norm(x) === norm(p)) === i);

  for (const path of ordered) {
    if (plan.size >= maxLinks) break;
    if (norm(currentPath) === norm(path)) continue;
    const index = blocks.findIndex((b, i) => {
      if (plan.has(i)) return false;
      const text =
        b.type === "p" ? b.text : b.type === "ul" || b.type === "ol" ? b.items.join(" ") : "";
      return text ? textMentions(path, text) : false;
    });
    if (index >= 0) plan.set(index, path);
  }
  return plan;
}


/** Forceert de link alleen op het eerste lijstitem dat het doel noemt. */
function forcedForItem(path: string | undefined, items: string[], index: number) {
  if (!path) return undefined;
  const first = items.findIndex((item) => textMentions(path, item));
  return first === index ? path : undefined;
}



/** Rendert redactionele blokken in een strak, leesbaar artikelraster. */
export function ArticleBody({
  blocks: rawBlocks,
  currentPath = "/",
  budget,
  priorityPaths,
  slots,
}: {
  blocks: ArticleBlock[];
  /** Componenten die op een `slot`-blok in de tekst worden geplaatst. */
  slots?: Record<string, React.ReactNode>;
  /** Huidig pad, zodat de tekst nooit naar zichzelf linkt. */
  currentPath?: string;
  /** Gedeeld linkbudget, zodat links over de hele pagina verspreid blijven. */
  budget?: LinkBudget;
  /** Money pages van dit artikel die voorrang krijgen. */
  priorityPaths?: readonly string[];
}) {
  const blocks = withPageImages(rawBlocks, currentPath);
  const linkBudget = budget ?? new LinkBudget(currentPath, 12, 3, 1, priorityPaths ?? []);

  const required = planRequiredLinks(blocks, currentPath, priorityPaths ?? []);
  return (
    <div className="space-y-5">
      {blocks.map((block, i) => {

        switch (block.type) {
          case "slot":
            return slots?.[block.name] ? <div key={i}>{slots[block.name]}</div> : null;
          case "h2":
            return (
              <h2
                key={i}
                id={block.id ?? slugify(block.text)}
                className="scroll-mt-32 pt-4 text-2xl font-bold tracking-tight text-foreground"
              >
                {block.text}
              </h2>
            );
          case "h3":
            return (
              <h3 key={i} id={slugify(block.text)} className="scroll-mt-32 pt-2 text-lg font-semibold text-foreground">
                {block.text}
              </h3>
            );
          case "p":
            return (
              <p key={i} className="text-[15px] leading-7 text-muted-foreground">
                {linkifyText(block.text, linkBudget, `p-${i}`, required.get(i))}
              </p>
            );
          case "ul":
            return (
              <ul key={i} className="space-y-2 pl-5">
                {block.items.map((item, ii) => (
                  <li key={item} className="list-disc text-[15px] leading-7 text-muted-foreground">
                    {linkifyText(item, linkBudget, `ul-${i}-${ii}`, forcedForItem(required.get(i), block.items, ii))}
                  </li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol key={i} className="space-y-2 pl-5">
                {block.items.map((item, ii) => (
                  <li key={item} className="list-decimal text-[15px] leading-7 text-muted-foreground">
                    {linkifyText(item, linkBudget, `ol-${i}-${ii}`, forcedForItem(required.get(i), block.items, ii))}
                  </li>
                ))}
              </ol>

            );
          case "quote":
            return (
              <blockquote
                key={i}
                className="border-l-4 border-success bg-secondary/50 px-5 py-4 text-[15px] italic leading-7 text-foreground"
              >
                {block.text}
                {block.source ? (
                  <footer className="mt-2 text-xs not-italic text-muted-foreground">— {block.source}</footer>
                ) : null}
              </blockquote>
            );
          case "callout":
            return (
              <div key={i} className="rounded-lg border border-border bg-card p-5">
                <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Info className="h-4 w-4 text-success" aria-hidden />
                  {block.title}
                </p>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{block.text}</p>
              </div>
            );
          case "table":
            return (
              <div key={i} className="cv-auto overflow-x-auto rounded-lg border border-border">
                <table className="w-full min-w-[560px] text-sm">
                  {block.caption ? (
                    <caption className="bg-secondary px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                      {block.caption}
                    </caption>
                  ) : null}
                  <thead className="bg-secondary text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      {block.head.map((h) => (
                        <th key={h} scope="col" className="px-4 py-3 font-semibold">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {block.rows.map((row, ri) => (
                      <tr key={ri} className="bg-card">
                        {row.map((cell, ci) => (
                          <td
                            key={ci}
                            className={ci === 0 ? "px-4 py-3 font-medium text-foreground" : "px-4 py-3 text-muted-foreground"}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case "image":
            return (
              <figure key={i} className="cv-auto overflow-hidden rounded-lg border border-border">
                <img src={block.src} alt={block.alt} width={1280} height={720} loading="lazy" decoding="async" className="aspect-video w-full object-cover" />
                {block.caption ? (
                  <figcaption className="bg-secondary px-4 py-2 text-xs text-muted-foreground">
                    {block.caption}
                  </figcaption>
                ) : null}
              </figure>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}

/** Inhoudsopgave op basis van de h2-koppen. */
export function ArticleToc({ blocks }: { blocks: ArticleBlock[] }) {
  const items = blocks.filter((b): b is Extract<ArticleBlock, { type: "h2" }> => b.type === "h2");
  if (items.length < 3) return null;
  return (
    <nav aria-label="Inhoudsopgave" className="rounded-lg border border-border bg-card p-5">
      <p className="text-sm font-semibold text-foreground">Inhoudsopgave</p>
      <ol className="mt-3 space-y-2">
        {items.map((item) => (
          <li key={item.text}>
            <a
              href={`#${item.id ?? slugify(item.text)}`}
              className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              {item.text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}
